import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { ThemeProvider } from "@emotion/react";
import { createTheme } from "@mui/material";
import Fields from "./fields";
import { formValues, shareProfileFormProps } from "./type";
import { defaultFormTheme } from "../../../../../atoms/defaultFormTheme";
import { useSnackbar } from "notistack";
import { RequestServer } from "../../../../../utils/services";
import { APIRoutes } from "../../../../../constants";
import { usePeMappingContext } from "../../helper";
import Loading from "../../../../../atoms/loading";
import { useFullPageLoading } from "../../../../../atoms/full-page-loading/loadingContext";
import { removeWhiteSpacesFromForm } from "../../../../../utils/utils";
import { useFetch } from "../../../../../utils/hooks/useFetch";
import { useGetParams } from "../../../../../utils/hooks/useGetParams";
import { useEffect, useState } from "react";

const ShareProfileForm = ({
  pe_id,
  expert_id,
  handleClose,
  handleSubmitClose,
  handleChange,
  designation,
  company,
  expertsData,
  archetypes,
  defaultValues, location, setPeDialog, isUsedInBulk, setCurrentExpert, isBulkLast, rows, group }: shareProfileFormProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { refetch } = usePeMappingContext();
  const { setLoading } = useFullPageLoading();
  const project_id = useGetParams("project_id");
  const current_row = rows?.find(d => d.pe_id == pe_id) || null;
  const [checkedAgendaIds, setCheckedAgendaIds] = useState<number[]>([]);
  const client_id = defaultValues?.projectDetails?.client_id || current_row?.client_id;

  const { data } = useFetch(APIRoutes.clients + "?id=" + client_id + "&show_columns=id,ask_for_govt_employee");
  const ask_for_govt_employee = data && data[0].ask_for_govt_employee;
  // Fetch agendas and responses
  const { formattedData: agendaResponseData } = useFetch(
    APIRoutes.PE_AGENDA + "?fk_project=" + project_id + "&fk_pe=" + pe_id + "&in___status=RespondedByExpert,RespondedByAdmin&embed=YES",
    {
      formatter: (data) => {
        const formattedData = data.map((item: any) => ({
          id: item.id,
          title: item.fk_agenda_value ? item.fk_agenda_value.title : "Title Not Provided",
          questions: item.agenda_responses,
          ...item,
        }))

        return formattedData
      }
    }
  );


  const onSubmit: SubmitHandler<formValues> = async (formData) => {

    const newFormData = removeWhiteSpacesFromForm(formData, [])

    if (!newFormData.snippets) {
      enqueueSnackbar("Please add Relevancy(ies) before sharing the profile with the client.", {
        variant: "warning"
      })
      return;
    }

    if (ask_for_govt_employee && formData.is_govt_employee && !formData.govt_employee) {
      enqueueSnackbar("Please select a government employee type before proceeding", {
        variant: "warning"
      })
      return;
    }

    const { description: snippet, heading: snippet_title } = JSON.parse(newFormData.snippets);

    const govt_employee = (ask_for_govt_employee && formData.is_govt_employee && formData.govt_employee) ? { value: formData.govt_employee, location: formData.govt_employee === "Non-US" ? formData.non_us_employee_location : null } : null;

    const payload: any = {
      action: "ShareProfile",
      id: pe_id,
      cost_price: newFormData.cost_price,
      selling_price: newFormData.selling_price,
      selling_price_currency: newFormData.currency.value,
      time_multiplier: newFormData.multiplier?.value,
      ...(newFormData.archetypes?.length && {
        archetypes: newFormData.archetypes.map(item => item.value)
      }),
      snippet,
      snippet_title,
      ...( agendaResponseData && agendaResponseData.length > 0 && {share_agenda_responses: newFormData.is_share_agenda === 'yes'}),
      govt_employee,
      do_not_share_outside: newFormData.do_not_share_outside
    };

    if (newFormData.is_share_agenda === "yes") {
      payload.pe_agenda_ids = checkedAgendaIds;
    }
    setLoading(true);
    try {
      const response = await RequestServer(
        APIRoutes.peMapping,
        "PATCH",
        payload
      );
      if (response.success) {
        enqueueSnackbar("Profile shared.", {
          variant: "success",
        });
        if (!isUsedInBulk) {
          handleSubmitClose();
          refetch();

          // opening Expert details to page - copy to clipboard
          setPeDialog((prev) => ({
            ...prev,
            actions: {
              ...prev.actions,
              shareProfile: {
                ...prev.actions.shareProfile,
                email_format: true,
                snippet: snippet,
                expert_id: expert_id,
                company: company,
                designation: designation,
                charges: `${newFormData.currency.value} ${newFormData.selling_price}`,
                meta: response?.data?.meta
              }
            }
          }))
        } else {
          setCurrentExpert && setCurrentExpert({
            email_format: true,
            snippet: snippet,
            expert_id: expert_id,
            company: company,
            designation: designation,
            charges: `${newFormData.currency.value} ${newFormData.selling_price}`,
            meta: response?.data?.meta,
            pe_id: pe_id,
            location: location,
            expert_name: expertsData.name || "",
            badge: rows ? (rows.find(r => r.expert_id === expert_id)?.badge || null) : null
          })
        }
      } else {
        enqueueSnackbar(response.message.toString(), { variant: "warning" });
      }
    } catch (err) {
      console.error({ err });
      enqueueSnackbar("Request failed.", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const methods = useForm({ defaultValues });

  const defaultTheme = createTheme(defaultFormTheme);

   useEffect(() => {
    if (agendaResponseData?.length) {
      const preCheckedIds = agendaResponseData
        .filter((item: any) => item.is_shared_with_client)
        .map((item: any) => item.id);
      setCheckedAgendaIds(preCheckedIds);
    }
  }, [agendaResponseData]);
  return (
    <FormProvider {...methods}>
      <ThemeProvider theme={defaultTheme}>
        <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
          {expert_id && expertsData ? (
            <Fields
              archetypes={archetypes}
              expert_id={expert_id}
              pe_id={pe_id}
              handleClose={handleClose}
              handleChange={handleChange}
              designation={designation}
              company={company}
              expertsData={expertsData}
              location={location}
              isUsedInBulk={isUsedInBulk}
              isBulkLast={isBulkLast}
              group={group}
              ask_for_govt_employee={ask_for_govt_employee}
              client_id={client_id}
              checkedAgendaIds={checkedAgendaIds}
              setCheckedAgendaIds={setCheckedAgendaIds}
              agendaResponseData={agendaResponseData}
            />
          ) : (
            <Loading loading />
          )}
        </form>
      </ThemeProvider>
    </FormProvider>
  );
};

export default ShareProfileForm;
